import { FilterChip } from 'components/UI/Responsive/FilterChips';
import { OrderIntentionStatusEnum } from 'enums/orderIntentionStatus.enum';
import { TFunction } from 'i18next';
import moment from 'moment';

export const ORDER_INTENTIONS_ADVANCED_FIELDS = [
  'Data.SiteId',
  'Data.ProductCategoryId',
  'Data.ProductGroupId',
  'Data.ProductId',
  'Data.Recipient',
  'Data.ProductQualityId',
  'Data.NatureOperationId',
  'Data.Origin',
  'Data.PublishedBy',
];

export const ORDER_INTENTIONS_CLEARED_ADVANCED_FILTERS = {
  'Data.NatureOperationId': undefined,
  'Data.Origin': undefined,
  'Data.ProductCategoryId': undefined,
  'Data.ProductGroupId': undefined,
  'Data.ProductId': undefined,
  'Data.ProductQualityId': undefined,
  'Data.PublishedBy': undefined,
  'Data.Recipient': undefined,
  'Data.SiteId': undefined,
};

type FilterValues = Record<string, unknown>;

const isFilled = (value: unknown) =>
  value !== undefined &&
  value !== null &&
  !Number.isNaN(value) &&
  String(value).trim() !== '';

export const countActiveAdvancedFilters = (values: FilterValues): number =>
  ORDER_INTENTIONS_ADVANCED_FIELDS.filter((field) => isFilled(values[field]))
    .length;

const DATE_TYPE_LABELS: Record<string, string> = {
  P: 'Publicação',
  E: 'Entrega',
  F: 'Faturamento',
};

const STATUS_OPTION_KEYS: Record<string, string> = {
  [OrderIntentionStatusEnum.CONFIRMADO]: 'generate',
  [OrderIntentionStatusEnum.CONFIRMADO_PARCIAL]: 'notGenerate',
  [OrderIntentionStatusEnum.CONFIRMADO_ALTERACAO]: 'changed',
  [OrderIntentionStatusEnum.PENDENTE]: 'open',
  [OrderIntentionStatusEnum.AGUARDANDO_GERACAO_PEDIDO]: 'process',
  [OrderIntentionStatusEnum.AGUARDANDO_PRODUTOR]: 'waitingProducer',
  [OrderIntentionStatusEnum.RECUSADO]: 'refused',
  [OrderIntentionStatusEnum.CANCELADO]: 'canceled',
  [OrderIntentionStatusEnum.EXPIRADO]: 'programmed',
  [OrderIntentionStatusEnum.ERRO]: 'error',
  [OrderIntentionStatusEnum.AGUARDANDO_PENDENTE]: 'waitingAndOpen',
};

export const describeAppliedPeriod = (
  values: FilterValues,
): string | undefined => {
  const date = values.date as [moment.Moment, moment.Moment] | null;
  if (!date?.length || !date[0]) return undefined;
  const typeLabel = DATE_TYPE_LABELS[String(values['Data.FilterBy'])] ?? 'Data';
  const start = moment(date[0]).format('DD/MM/YYYY');
  const end = moment(date[1]).format('DD/MM/YYYY');
  return start === end
    ? `${typeLabel} em ${start}`
    : `${typeLabel} de ${start} a ${end}`;
};

export const describeStatus = (t: TFunction, status: unknown) =>
  STATUS_OPTION_KEYS[String(status)]
    ? t(
        `myOrderIntentions.formFilter.statusOptions.${
          STATUS_OPTION_KEYS[String(status)]
        }`,
      )
    : String(status);

interface ChipOptions {
  t: TFunction;
  customerLabel?: string;
  onRemoveStatus: () => void;
  onRemoveAdvanced: () => void;
}

export const buildOrderIntentionsFilterChips = (
  values: FilterValues,
  { t, customerLabel, onRemoveStatus, onRemoveAdvanced }: ChipOptions,
): FilterChip[] => {
  const chips: FilterChip[] = [];
  const period = describeAppliedPeriod(values);
  if (period) {
    chips.push({
      key: 'period',
      name: 'Período',
      text: period,
      highlighted: true,
    });
  }

  if (customerLabel) {
    chips.push({ key: 'customer', name: 'Chave', text: customerLabel });
  }

  const status = values['Data.Status'];
  if (status === OrderIntentionStatusEnum.AGUARDANDO_PENDENTE) {
    chips.push({
      key: 'status',
      name: 'Status',
      text: describeStatus(t, status),
    });
  } else if (isFilled(status)) {
    chips.push({
      key: 'status',
      name: 'Status',
      text: describeStatus(t, status),
      onRemove: onRemoveStatus,
    });
  }

  const advancedCount = countActiveAdvancedFilters(values);
  if (advancedCount > 0) {
    chips.push({
      key: 'advanced',
      name: 'Filtros avançados',
      text:
        advancedCount === 1
          ? '1 filtro avançado'
          : `${advancedCount} filtros avançados`,
      onRemove: onRemoveAdvanced,
    });
  }

  return chips;
};
